type FieldType = '$' | '%' | 'str' | 'bool' | 'array' | 'int';

export type SchemaField = {
    t: FieldType;
    yearly?: boolean;
    field?: string;
};

export type ExtendedSchemaField = {
    display: string;
    dynDisplayPrefix?: string;
    dynDisplay?: string;
    t: FieldType;
    scale?: number;
};

// Break FieldsSchema into sub-structs based on categories
export const RevenueFields = {
    net_revenue_per_year: { t: '$', yearly: true, field: 'revenue' },
    top_10_customers_revenue_percentage: {
        t: '%',
        yearly: true,
        field: 'percentage_of_revenue'
    },

    top_1_customers_revenue_percentage: {
        t: '%',
        yearly: true,
        field: 'percentage_of_revenue'
    },
    acquisition_ebitda_per_year: {
        t: '$',
        yearly: true,
        field: 'acquisition_ebitda'
    },
    acquisition_revenue_per_year: {
        t: '$',
        yearly: true,
        field: 'acquisition_revenue'
    },
    gross_profit_per_year: { t: '$', yearly: true, field: 'gross_profit' },
    fixed_asset_acquisitions_revenue_per_year: {
        t: '$',
        yearly: true,
        field: 'acquisition_rev'
    },
    recurring_revenue_percent_per_year: {
        t: '$',
        yearly: true,
        field: 'recurring_revenue_percent'
    },
    gross_retention_lost_only_per_year: {
        t: '$',
        yearly: true,
        field: 'gross_retention_lost_only'
    },
    net_retention_yearly: {
        t: '$',
        yearly: true,
        field: 'net_retention_percent'
    }
} as const;

export const EBITDAFields = {
    ebitda_per_year: { t: '$', yearly: true, field: 'ebitda' },
    adjusted_ebitda_per_year: {
        t: '$',
        yearly: true,
        field: 'adjusted_ebitda'
    }
    // ebitda_unadjusted: {
    //     display: 'Unadjusted EBITDA',
    //     t: '$',
    //     yearly: true,
    //     field: 'ebitda_unadjusted'
    // },
    // ebitda_pro_forma: {
    //     display: 'Pro Forma EBITDA',
    //     t: '$',
    //     yearly: true,
    //     field: 'ebitda_pro_forma'
    // },
    // ebitda_adjusted: {
    //     display: 'Adjusted EBITDA',
    //     t: '$',
    //     yearly: true,
    //     field: 'ebitda_adjusted'
    // },
    // ebitda_runrate: {
    //     display: 'Runrate Adjusted EBITDA',
    //     t: '$',
    //     yearly: true,
    //     field: 'ebitda_runrate'
    // },
    // ebitda_annualized: {
    //     display: 'Annualized Adjusted EBITDA',
    //     t: '$',
    //     yearly: true,
    //     field: 'ebitda_annualized'
    // },
    // ebitda_cash: {
    //     display: 'Cash EBITDA',
    //     t: '$',
    //     yearly: true,
    //     field: 'ebitda_cash'
    // }
} as const;

export const CapExFields = {
    capitalized_software_override: {
        t: '$',
        yearly: true,
        field: 'capitalized_software_override'
    },

    capitalized_software: {
        display: 'Capitalized Software',
        t: '$',
        yearly: true,
        field: 'capitalized_software'
    },
    capitalized_application_development: {
        display: 'Capitalized Application Development',
        t: '$',
        yearly: true,
        field: 'capitalized_application_development'
    },
    capitalized_rnd: {
        display: 'Capitalized R&D',
        t: '$',
        yearly: true,
        field: 'capitalized_rnd'
    }
} as const;

export const OtherFields = {
    capex_per_year: { t: '$', yearly: true, field: 'capex' },
    maintenance_capex_per_year: {
        t: '$',
        yearly: true,
        field: 'maintenance_capex'
    },
    growth_capex_per_year: { t: '$', yearly: true, field: 'growth_capex' },
    total_current_assets_yearly: {
        t: '$',
        yearly: true,
        field: 'total_current_assets'
    },
    total_current_liabilities_yearly: {
        t: '$',
        yearly: true,
        field: 'total_current_liabilities'
    },
    historical_market_size: {
        t: '%',
        yearly: true,
        field: 'historic_market_size'
    },
    projected_market_size: {
        t: '%',
        yearly: true,
        field: 'projected_market_size'
    },
    list_years_actualized_revenue: { t: 'array' },
    list_years_projected_revenue: { t: 'array' },
    recurring_revenue_summary: { t: 'bool' },
    company_name: { t: 'str' },
    company_overview: { t: 'str' },
    company_main_market: { t: 'str' },
    company_ceo: { t: 'str' },
    hq_location: { t: 'str' },
    year_founded: { t: 'year' },
    industry: { t: 'str' },
    industrySubType: { t: 'str' },
    subscription_revenue: { t: 'bool' },
    recurring_revenue_stream: { t: 'bool' },
    contractual_revenue: { t: 'bool' },
    total_addressable_market: { t: '$' },
    market_share: { t: '%' },
    historical_market_growth: { t: '%' },
    projected_market_growth: { t: '%' },
    last_full_fiscal_year_reported: { t: 'int' }
} as const;

export const FieldsSchema = {
    ...RevenueFields,
    ...EBITDAFields,
    ...CapExFields,
    ...OtherFields
} as const;

export type SchemaFieldKeys = keyof typeof FieldsSchema;

export const ExtendedFieldsSchema = {
    extended_selected_ebitda: {
        t: 'str',
        display: 'EBITDA Used for Comparison'
    },
    organic_cagr_N_years: {
        t: '%',
        display: 'Year(s) Historical Organic Revenue CAGR',
        dynDisplayPrefix: '$DATA_PICK_KEY-'
    },
    organic_cagr_4_years: {
        t: '%',
        display: '4-Year Historical Organic Revenue CAGR'
    },
    organic_cagr_3_years: {
        t: '%',
        display: '3-Year Historical Organic Revenue CAGR'
    },
    projected_organic_cagr_N_years: {
        t: '%',
        display: 'Year(s) Projected Organic Revenue CAGR',
        dynDisplayPrefix: '$DATA_PICK_KEY-'
    },
    projected_organic_cagr_3_years: {
        t: '%',
        display: '3-Year Projected Organic Revenue CAGR'
    },
    projected_organic_cagr_4_years: {
        t: '%',
        display: '4-Year Projected Organic Revenue CAGR'
    },
    projected_organic_cagr_N_years_minus_tam_growth: {
        t: '%',
        display: 'Relative Growth Rate (Company Growth less Market Growth)'
    },
    adj_ebitda_margin: {
        t: '%',
        display: 'Adjusted EBITDA Margin',
        dynDisplay: '%SELECTED_EBITDA% Margin'
    },
    adj_ebitda_margin_trend: {
        t: '%',
        display: 'Adjusted Ebitda Margin versus Two Years Ago',
        dynDisplay: '%SELECTED_EBITDA% Margin versus Two Years Ago'
    },
    adj_ebitda: {
        t: '$',
        display: 'Adjusted Ebitda',
        dynDisplay: '%SELECTED_EBITDA%'
    },
    gross_retention: { t: '%', display: 'Gross Retention Rate' },
    net_retention: { t: '%', display: 'Net Retention Rate', scale: 100 },
    ebitda_magnatitude_adjustment: {
        t: '%',
        display: '% of Adjusted EBITDA from Adjustments'
    },
    fcf_conversion_ebitda: { t: '%', display: '' },
    nwc_as_percent_of_revenue: {
        t: '%',
        display: 'New Working Capital, as a % of Revenue'
    },
    free_cash_flow_conversion: { t: '%', display: 'Free Cash Flow Conversion' },
    total_capex_by_rev: { t: '%', display: 'CapEx, as a % of Revenue' },
    total_addressable_market: {
        t: '$',
        display: 'Total Addressable Market Size'
    },
    historical_market_growth: {
        t: '%',
        display: 'Historical Market Growth Rate',
        scale: 100
    },
    projected_market_growth: {
        t: '%',
        display: 'Projected Market Growth Rate',
        scale: 100
    },
    recurring_revenue_summary: { t: 'bool', display: 'Contractual Revenue' },
    gross_profit_margin: { t: '%', display: 'Gross Profit Margin' },
    gross_profit: { t: '$', display: 'Gross Profit' },
    industry: { t: 'str', display: 'Industry' },
    industrySubType: { t: 'str', display: 'Sub-Industry' },
    company_ceo: { t: 'str', display: 'CEO' },
    hq_location: { t: 'str', display: 'HQ Location' },
    year_founded: { t: 'str', display: 'Founded' },
    last_full_fiscal_year_reported: { t: 'str', display: 'Actuals As Of' },
    revenue_latest: { t: '$', display: 'Revenue' }
} as const;
export type ExtendedSchemaKeys = keyof typeof ExtendedFieldsSchema;

// Add type definitions for each schema fields object
export type RevenueFieldsKeys = keyof typeof RevenueFields;
export type EBITDAFieldsKeys = keyof typeof EBITDAFields;
export type CapExFieldsKeys = keyof typeof CapExFields;
export type OtherFieldsKeys = keyof typeof OtherFields;
